/**
 * Speech Therapy Discharge (Non-Visit) — options + empty payload.
 * Common header (1–6) via StNotesCommonHeader; clinical sections 7–17
 * per ST-DCH-NV CMS mockup.
 */

import { emptyStCommonHeader } from "./stNotesCommonHeaderUtils";

export const DISCIPLINE_OPTIONS = ["Speech-Language Pathology"];

/** Check-one discharge types from ST non-visit mockup. */
export const DISCHARGE_TYPE_OPTIONS = [
  "Goals Met",
  "Maximum Potential Achieved",
  "Patient Request / Refusal",
  "Transfer to Another Agency",
  "Transfer to Hospice",
  "No Longer Homebound / Moved",
  "Other (Specify)",
];

export const ST_SERVICE_REASON_OPTIONS = [
  "Aphasia",
  "Cognitive-Communication Disorder",
  "Dysphagia / Swallowing Disorder",
  "Speech Sound Disorder / Dysarthria",
  "Voice Disorder",
  "Social / Pragmatic Communication Disorder",
  "Other",
];

export const GOAL_OUTCOME_STATUS_OPTIONS = [
  "Met",
  "Partially Met",
  "Not Met",
  "Unable to Assess",
];

export const OVERALL_OUTCOME_OPTIONS = [
  "Goals Met",
  "Maximum Potential Reached",
  "Partially Met",
  "Not Met / Unable to Assess",
];

export const FUNCTIONAL_DOMAIN_ROWS = [
  "Receptive Language",
  "Expressive Language",
  "Speech Intelligibility",
  "Voice / Resonance",
  "Cognitive-Communication",
  "Social Communication",
  "Reading",
  "Writing",
  "Swallowing / Dysphagia",
  "AAC Use",
];

/** Ind / Sup / Mod / Max / Dep from mockup. */
export const ASSIST_LEVEL_OPTIONS = [
  { key: "Ind", label: "Ind" },
  { key: "Sup", label: "Sup" },
  { key: "Mod", label: "Mod" },
  { key: "Max", label: "Max" },
  { key: "Dep", label: "Dep" },
];

export const ASSIST_LEVEL_KEYS = ASSIST_LEVEL_OPTIONS.map((o) => o.key);

export const YES_NO_OPTIONS = ["Yes", "No"];

export const YES_NO_NA_OPTIONS = ["Yes", "No", "N/A"];

export const HOMEBOUND_REASON_OPTIONS = [
  "Requires assistance of another person to leave home",
  "Requires supportive device and assistance",
  "Leaving home is medically contraindicated",
  "Absences are infrequent / short duration / for medical treatment",
];

export const HOME_ENVIRONMENT_OPTIONS = [
  "Safe",
  "Needs Modifications",
  "Unsafe",
];

export const CAREGIVER_INVOLVEMENT_OPTIONS = [
  "Independent",
  "Consistent Support",
  "Intermittent Support",
  "None Available",
];

export const SAFETY_CONCERN_OPTIONS = [
  "Fall Risk",
  "Aspiration Risk",
  "Medication Safety",
  "Communication Barriers",
  "Environmental Hazards",
];

export const EDUCATION_PROVIDED_OPTIONS = [
  "Swallowing Strategies",
  "Communication Strategies",
  "Home Exercise Program",
  "Safety Education",
  "Diet Modifications",
  "Caregiver Training",
];

export const FOLLOW_UP_OPTIONS = [
  "Outpatient ST",
  "Community Resources",
  "Physician Follow-Up",
  "No Further ST Recommended",
];

export const MEDICAL_NECESSITY_OPTIONS = [
  "Patient met goals / no longer needs skilled ST",
  "Maximum potential achieved",
  "Patient request / refusal",
  "Transfer to another provider / hospice",
  "No longer homebound / moved",
  "Physician discontinued therapy",
];

export const PHYSICIAN_NOTIFY_METHOD_OPTIONS = [
  "Phone",
  "Fax",
  "Electronic",
  "Other",
];

/** Row-major 3-column CMS checklist from mockup. */
export const CMS_CHECKLIST_OPTIONS = [
  "Patient identified (name/MRN/DOB)",
  "SOC date documented",
  "Certification period documented",
  "Primary diagnosis (ICD-10) documented",
  "Reason for ST services documented",
  "Goals and outcomes documented",
  "Functional status at discharge documented",
  "Homebound status addressed",
  "Safety / environment addressed",
  "Discharge planning / education documented",
  "Medical necessity / summary completed",
  "Physician notification documented",
  "Clinician signature / credentials present",
  "Date of discharge documented",
  "Non-visit rationale documented",
  "Plan of care reviewed",
  "All required fields completed",
  "Record ready for agency QA review",
];

export const ST_DNV_ARRAY_KEYS = [
  "stServiceReasons",
  "homeboundReasons",
  "homeEnvironment",
  "caregiverInvolvement",
  "safetyConcerns",
  "educationProvided",
  "followUpRecommended",
  "medicalNecessityReasons",
  "physicianNotifyMethods",
  "cmsChecklist",
];

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

export const today = () => new Date().toISOString().slice(0, 10);

export const emptyGoalOutcomeRow = (n = 1) => ({
  id: `goal-${n}`,
  included: false,
  description: "",
  outcome: "",
  dateMet: "",
});

export const emptyGoalOutcomes = () =>
  [1, 2, 3, 4, 5].map((n) => emptyGoalOutcomeRow(n));

export const mergeGoalOutcomes = (loaded) => {
  const list = Array.isArray(loaded) ? loaded : [];
  return [1, 2, 3, 4, 5].map((n) => {
    const row = list[n - 1] || {};
    const outcome = String(row.outcome || "");
    return {
      id: row.id || `goal-${n}`,
      included: Boolean(row.included),
      description: String(row.description || ""),
      outcome: GOAL_OUTCOME_STATUS_OPTIONS.includes(outcome) ? outcome : "",
      dateMet: String(row.dateMet || ""),
    };
  });
};

export const emptyFunctionalDomain = () => ({ soc: "", discharge: "" });

export const emptyFunctionalStatus = () =>
  Object.fromEntries(
    FUNCTIONAL_DOMAIN_ROWS.map((row) => [row, emptyFunctionalDomain()]),
  );

export const mergeFunctionalStatus = (loaded) => {
  const src = loaded && typeof loaded === "object" ? loaded : {};
  return Object.fromEntries(
    FUNCTIONAL_DOMAIN_ROWS.map((row) => {
      const cell = src[row] || {};
      const soc = String(cell.soc || "");
      const discharge = String(cell.discharge || "");
      return [
        row,
        {
          soc: ASSIST_LEVEL_KEYS.includes(soc) ? soc : "",
          discharge: ASSIST_LEVEL_KEYS.includes(discharge) ? discharge : "",
        },
      ];
    }),
  );
};

export const emptyStDischargeNonVisitPayload = () => ({
  billingCode: "G0153",
  timeIn: "",
  timeOut: "",
  dischargeDate: today(),
  commonHeader: emptyStCommonHeader(),

  // —— 7. Discharge Information ——
  discipline: "Speech-Language Pathology",
  dischargeType: "",
  dischargeTypeOther: "",
  dischargeTypeExplain: "",

  // —— 8. Primary Reason for HH ——
  primaryHhDiagnosisCode: "",
  primaryHhDescription: "",
  stOrderedForPrimary: "",
  stOrderedExplain: "",

  // —— 9. Reason for ST Services ——
  stServiceReasons: [],
  stServiceOtherDetails: "",

  // —— 10. Goals and Outcomes ——
  goalOutcomes: emptyGoalOutcomes(),
  overallOutcome: "",
  outcomeRationale: "",

  // —— 11. Patient Status at Discharge ——
  functionalStatus: emptyFunctionalStatus(),
  swallowingSafety: "",
  dietConsistency: "",
  communicationEffectiveness: "",
  participationInAdls: "",
  caregiverCapacity: "",
  painLevel: null,
  statusComments: "",

  // —— 12. Homebound ——
  remainsHomebound: "",
  homeboundReasons: [],
  homeboundComments: "",

  // —— 13. Safety & Environment ——
  homeEnvironment: [],
  caregiverInvolvement: [],
  caregiverManageNeeds: "",
  safetyConcerns: [],
  safetyComments: "",

  // —— 14. Discharge Planning ——
  educationProvided: [],
  understandingDemonstrated: "",
  followUpRecommended: [],
  instructionsProvided: "",
  planningComments: "",

  // —— 15. Medical Necessity ——
  medicalNecessityReasons: [],
  dischargeSummaryNarrative: "",

  // —— 16. Physician Notification ——
  physicianNotified: "",
  physicianNotifiedDate: "",
  physicianNotifyMethods: [],
  physicianNotifyComments: "",

  // —— 17. CMS Checklist ——
  cmsChecklist: [],
});
